import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import ConfirmDeleteModal from '../ConfirmDeleteModal';

/*
 * Linked systems -- the lab servers this desktop talks to over pitriot-link:
 * a MYCELIUM research server (literature reviews, proposals, media) or a MABP
 * bioreactor rig. It was a page of its own (/links, now a redirect here) and
 * lives on the bioreactor page, which shows what a paired rig reports.
 *
 * Pairing is trust on first use, confirmed by a person: PITRIOT fetches the
 * server's CA certificate, shows its fingerprint, and pins it only once the
 * manager says it matches the one the server printed. The link token is
 * stored encrypted on this computer (routes/lab_links.py); the browser sends
 * it once and never sees it again.
 *
 * The page reads GET /links once and hands every link here (`links`); a 501
 * there -- the cloud, where links do not exist -- is the page's "desktop only"
 * card, and this panel is not drawn at all. `canPair`: who pairs and
 * unpairs. Since 2026-10-13 lab_links.py takes one scope for every route,
 * bioreactor:access (a system admin or a collection manager), so whoever may
 * open the page may pair; the page passes can(user, 'bioreactor:access').
 */

// routes/lab_links.py KINDS, with the prefix each kind's tokens carry.
export const KINDS = {
  mycelium: { prefix: 'myc_', address: 'https://mycelium.local:8443' },
  mabp: { prefix: 'mabp_', address: 'https://mabp.local' },
};

const errorText = (e) => e?.response?.data?.error || e?.message || String(e);

const card = { background: 'var(--c-surface)', border: '1px solid var(--c-border)', borderRadius: 10,
               padding: 16, minWidth: 0 };
const field = { display: 'block', width: '100%', maxWidth: 380, marginTop: 4, boxSizing: 'border-box' };
const muted = { fontSize: 13, color: 'var(--c-text-muted-fg)' };

const STATE_TONE = {
  connected: ['var(--c-primary-soft-6)', 'var(--c-success-fg)'],
  unreachable: ['var(--c-danger-soft-3)', 'var(--c-danger-fg)'],
};

function StateChip({ state, t }) {
  const [bg, fg] = STATE_TONE[state] || ['var(--c-warning-soft)', 'var(--c-warning-fg)'];
  return (
    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                   background: bg, color: fg }}>
      {t(`links.state.${state}`, { defaultValue: state })}
    </span>
  );
}

/** Pairs either kind of system; the token is checked against its kind's prefix before it is sent. */
export function PairLinkForm({ onPaired, t, defaultKind = 'mycelium' }) {
  const [kind, setKind] = useState(KINDS[defaultKind] ? defaultKind : 'mycelium');
  const [baseUrl, setBaseUrl] = useState(KINDS[kind].address);
  const [preview, setPreview] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [label, setLabel] = useState('');
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const chooseKind = (next) => {
    // A different kind is a different server: start the checks again.
    if (baseUrl === KINDS[kind].address) setBaseUrl(KINDS[next].address);
    setKind(next); setPreview(null); setConfirmed(false); setError(null);
  };

  const doPreview = async () => {
    setBusy(true); setError(null); setPreview(null); setConfirmed(false);
    try {
      const res = await api.post('/links/preview', { kind, base_url: baseUrl.trim() });
      setPreview(res.data);
    } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };

  const tokenProblem = token && !token.trim().startsWith(KINDS[kind].prefix)
    ? t('links.pair.wrongPrefix', { prefix: KINDS[kind].prefix }) : null;

  const doPair = async () => {
    setBusy(true); setError(null);
    try {
      await api.post('/links', { kind, label: label.trim() || preview.base_url, base_url: preview.base_url,
                                 ca_sha256: preview.ca_sha256, token: token.trim() });
      setToken(''); setPreview(null); setConfirmed(false); setLabel('');
      onPaired();
    } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };

  return (
    <div style={{ borderTop: '1px solid var(--c-border)', paddingTop: 12, marginTop: 12 }} data-testid="pair-link-form">
      <h4 style={{ margin: '0 0 4px', fontSize: 14 }}>{t('links.pair.title')}</h4>
      <p style={{ ...muted, marginTop: 0 }}>{t('links.pair.intro')}</p>
      <label style={{ display: 'block', fontSize: 13 }}>
        {t('links.pair.kind')}
        <select value={kind} onChange={e => chooseKind(e.target.value)} title={t('links.tip.kind')} style={field}
                data-testid="pair-kind">
          {Object.keys(KINDS).map(k => <option key={k} value={k}>{t(`links.kind.${k}`)}</option>)}
        </select>
      </label>
      <label style={{ display: 'block', fontSize: 13, marginTop: 8 }}>
        {t('links.pair.address')}
        <input value={baseUrl} onChange={e => { setBaseUrl(e.target.value); setPreview(null); }}
               title={t('links.tip.address')} style={field} dir="ltr" data-testid="pair-address" />
      </label>
      <button type="button" onClick={doPreview} disabled={busy || !baseUrl.trim()} title={t('links.tip.preview')}
              style={{ marginTop: 8 }} data-testid="pair-preview">
        {t('links.pair.preview')}
      </button>

      {preview && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13 }}>{t('links.pair.fingerprint')}</div>
          <code dir="ltr" data-testid="pair-fingerprint"
                style={{ display: 'block', fontSize: 12, wordBreak: 'break-all', padding: 8,
                         background: 'var(--c-bg-subtle)', borderRadius: 6 }}>
            {preview.ca_fingerprint}
          </code>
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, marginTop: 8 }}>
            <input type="checkbox" checked={confirmed} title={t('links.tip.confirm')} data-testid="pair-confirm"
                   onChange={e => setConfirmed(e.target.checked)} />
            {t(`links.pair.confirm_${kind}`)}
          </label>
          <label style={{ display: 'block', fontSize: 13, marginTop: 8 }}>
            {t('links.pair.label')}
            <input value={label} maxLength={80} onChange={e => setLabel(e.target.value)} title={t('links.tip.label')}
                   style={field} data-testid="pair-label" />
          </label>
          <label style={{ display: 'block', fontSize: 13, marginTop: 8 }}>
            {t('links.pair.token')}
            <input type="password" autoComplete="off" value={token}
                   title={t('links.tip.token', { prefix: KINDS[kind].prefix })}
                   onChange={e => setToken(e.target.value)} style={field} dir="ltr" data-testid="pair-token" />
          </label>
          {tokenProblem && <div style={{ color: 'var(--c-warning-fg)', fontSize: 13 }}>{tokenProblem}</div>}
          <button type="button" onClick={doPair} disabled={busy || !confirmed || !token.trim() || !!tokenProblem}
                  title={t('links.tip.pair')} style={{ marginTop: 8 }} data-testid="pair-submit">
            {t('links.pair.submit')}
          </button>
        </div>
      )}
      {error && <div role="alert" style={{ color: 'var(--c-danger-2-fg)', fontSize: 13, marginTop: 8 }}>{error}</div>}
    </div>
  );
}

export default function LinkedSystemsPanel({ links, canPair, onChanged, defaultKind }) {
  const { t } = useTranslation('bioreactor');
  const [states, setStates] = useState({});
  const [unpairing, setUnpairing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [unpairError, setUnpairError] = useState('');

  const checkStatus = useCallback(async (linkId) => {
    setStates(s => ({ ...s, [linkId]: { state: 'checking' } }));
    try {
      const res = await api.get(`/links/${linkId}/status`);
      setStates(s => ({ ...s, [linkId]: res.data }));
    } catch (e) {
      setStates(s => ({ ...s, [linkId]: { state: 'unreachable', message: errorText(e) } }));
    }
  }, []);

  useEffect(() => { links.forEach(l => checkStatus(l.link_id)); }, [links, checkStatus]);

  const unpair = async () => {
    setBusy(true); setUnpairError('');
    try {
      await api.delete(`/links/${unpairing.link_id}`);
      setUnpairing(null);
      onChanged();
    } catch (e) { setUnpairError(errorText(e)); } finally { setBusy(false); }
  };

  return (
    <section style={card} aria-labelledby="linked-systems-title" data-testid="linked-systems">
      <h3 id="linked-systems-title" style={{ margin: '0 0 6px', fontSize: 15 }}>{t('links.title')}</h3>
      <p style={{ ...muted, marginTop: 0 }}>{t('links.intro')}</p>
      {links.length === 0 && <p style={muted}>{t('links.none')}</p>}
      {links.map(l => {
        const st = states[l.link_id];
        return (
          <div key={l.link_id} data-testid={`link-${l.link_id}`}
               style={{ borderTop: '1px solid var(--c-border)', padding: '10px 0', display: 'flex',
                        flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ minWidth: 0 }}>
              <strong>{l.label}</strong> <span style={muted}>· {t(`links.kind.${l.kind}`)}</span>
              <div dir="ltr" style={{ ...muted, wordBreak: 'break-all' }}>{l.base_url}</div>
              {l.contract && <div style={muted}>{t('links.contract', { contract: l.contract })}</div>}
              {st?.message && <div style={{ fontSize: 13, color: 'var(--c-danger-fg)' }}>{st.message}</div>}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {st && <StateChip state={st.state} t={t} />}
              <button type="button" onClick={() => checkStatus(l.link_id)} title={t('links.tip.recheck')}
                      data-testid={`recheck-${l.link_id}`}>
                {t('links.recheck')}
              </button>
              {canPair && (
                <button type="button" onClick={() => { setUnpairError(''); setUnpairing(l); }}
                        title={t('links.tip.unpair')} data-testid={`unpair-${l.link_id}`}>
                  {t('links.unpair')}
                </button>
              )}
            </div>
          </div>
        );
      })}

      {canPair
        ? <PairLinkForm onPaired={onChanged} t={t} defaultKind={defaultKind} />
        : <p style={{ ...muted, marginBottom: 0 }}>{t('links.askManager')}</p>}

      <ConfirmDeleteModal
        open={!!unpairing}
        title={unpairing ? t('links.unpairTitle', { label: unpairing.label }) : ''}
        consequences={[t('links.unpairConsequence')]}
        deleting={busy}
        error={unpairError}
        confirmLabel={t('links.unpair')}
        confirmingLabel={t('links.unpairing')}
        onConfirm={unpair}
        onCancel={() => setUnpairing(null)}
      />
    </section>
  );
}
